import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useEffect, useState } from "react";

type VariantOption = { name: string; value: string };

type Variant = {
  id: string;
  selectedOptions: VariantOption[];
  inventory_quantity: number;
  inventoryPolicy?: string;
  image?: string;
};

type Product = {
  productId: string;
  productTitle: string;
  image?: string;
  variants: Variant[];
  options: { name: string; values: string[]; position: number }[];
};

function findVariant(variants: Variant[], selectedOptions: Record<string, string>): Variant | undefined {
  return variants.find((v) =>
    v.selectedOptions.every((o) => selectedOptions[o.name] === o.value),
  );
}

function isVariantAvailable(v: Variant, minimalStock: number): boolean {
  return v.inventoryPolicy === "CONTINUE" || v.inventory_quantity > minimalStock;
}

export function getUniqueValues(variants: Variant[], optName: string): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const v of variants) {
    const opt = v.selectedOptions.find((o) => o.name === optName);
    if (opt && !seen.has(opt.value)) {
      seen.add(opt.value);
      values.push(opt.value);
    }
  }
  return values;
}

export function isValueAvailable(
  variants: Variant[],
  selectedOptions: Record<string, string>,
  optName: string,
  value: string,
  minimalStock: number,
): boolean {
  const testOpts = { ...selectedOptions, [optName]: value };
  const matched = findVariant(variants, testOpts);
  return matched ? isVariantAvailable(matched, minimalStock) : false;
}

type UseVariantSelectorResult = {
  products: Product[];
  selectedProductId: string;
  selectedOptions: Record<string, string>;
  selectedProduct: Product | undefined;
  selectedImageUrl: string | undefined;
  selectableOptions: Product["options"];
  hasRealChoice: boolean;
  minimalStock: number;
  handleProductChange: (productId: string) => void;
  handleOptionChange: (name: string, value: string) => void;
};

export function useVariantSelector(
  orderId: string,
  lineItemId: string,
  shopId: string,
  onVariantChange: (variantId: string | null) => void,
): UseVariantSelectorResult {
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);

  const minimalStock: number = (shop as any)?.exchange?.minimalStock ?? 0;
  const collection = (order as any)?.variantByLineItemId?.[lineItemId] as
    | { products: Product[] }
    | undefined;
  const products = collection?.products ?? [];
  const firstProduct = products[0];

  const firstAvailableVariant = firstProduct?.variants.find((v) =>
    isVariantAvailable(v, minimalStock),
  );

  const initialProductId = firstProduct?.productId ?? "";
  const initialOpts: Record<string, string> = {};
  if (firstAvailableVariant) {
    for (const o of firstAvailableVariant.selectedOptions) {
      initialOpts[o.name] = o.value;
    }
  }

  const [selectedProductId, setSelectedProductId] = useState(initialProductId);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(initialOpts);

  const selectedProduct = products.find((p) => p.productId === selectedProductId) ?? firstProduct;

  const selectableOptions = (selectedProduct?.options ?? []).filter((opt) => {
    const values = getUniqueValues(selectedProduct?.variants ?? [], opt.name);
    const realValues = values.filter((v) => v !== "Default Title");
    return realValues.length > 1;
  });

  const hasRealChoice = products.length > 1 || selectableOptions.length > 0;

  const selectedVariant = selectedProduct
    ? findVariant(selectedProduct.variants, selectedOptions)
    : undefined;
  const selectedImageUrl = selectedVariant?.image || selectedProduct?.image;

  useEffect(() => {
    if (!firstAvailableVariant) {
      onVariantChange(null);
      return;
    }
    const matched = selectedProduct
      ? findVariant(selectedProduct.variants, selectedOptions)
      : undefined;
    onVariantChange(
      matched && isVariantAvailable(matched, minimalStock) ? matched.id : null,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleProductChange(productId: string) {
    const product = products.find((p) => p.productId === productId);
    if (!product) return;
    setSelectedProductId(productId);
    const firstAvailable = product.variants.find((v) => isVariantAvailable(v, minimalStock));
    const opts: Record<string, string> = {};
    if (firstAvailable) {
      for (const o of firstAvailable.selectedOptions) opts[o.name] = o.value;
    }
    setSelectedOptions(opts);
    onVariantChange(firstAvailable ? firstAvailable.id : null);
  }

  function handleOptionChange(name: string, value: string) {
    const next = { ...selectedOptions, [name]: value };
    setSelectedOptions(next);
    const matched = selectedProduct ? findVariant(selectedProduct.variants, next) : undefined;
    onVariantChange(
      matched && isVariantAvailable(matched, minimalStock) ? matched.id : null,
    );
  }

  return {
    products,
    selectedProductId,
    selectedOptions,
    selectedProduct,
    selectedImageUrl,
    selectableOptions,
    hasRealChoice,
    minimalStock,
    handleProductChange,
    handleOptionChange,
  };
}
